import { PropertyDetailsFragment } from '__generated__/athena';

import { isBuyDirectEligible } from './buyDirectHelpers';

type TourKind = 'selfTour' | 'tourWithAgent';

export const TOUR_ACTION_TEXT_NON_BUY_DIRECT: Record<TourKind, string> = {
  selfTour: 'Self-tour',
  tourWithAgent: 'Share home',
};

export const TOUR_ACTION_TEXT_BUY_DIRECT: Record<TourKind, string> = {
  selfTour: 'Self-tour',
  tourWithAgent: 'Tour with agent',
};

interface PropertyActionabilityResult {
  // Whether this property is available to be bought
  isActionable: boolean;
  // Whether property can be self-toured or has to be with an agent
  tourKind: TourKind | null;
  // Whether property is eligible for BuyDirect checkout (based on our rollout status)
  showBuyDirect: boolean;
  // Whether to disable the BuyDirect button even if it's shown
  disableBuyDirect: boolean;
}

/**
 * Determines if a property is actionable (can be toured or purchased) and what type of tour is available.
 *
 * Badge meanings (determined in athena transformToDisplayBadge):
 * - ON_THE_MARKET: Property is for sale AND canSelfTour=true
 * - TRADITIONAL: Property is for sale BUT canSelfTour=false (MLS listing, requires agent)
 * - UNKNOWN: Property status unclear, check canSelfTour flag
 *
 * @param property - The property to check
 * @returns Object with isActionable flag, tour kind (null if not actionable), and whether to show buy direct
 */
export const determinePropertyActionability = (
  property: PropertyDetailsFragment,
): PropertyActionabilityResult => {
  const isUnknownRequiresAgent = property.displayBadge === 'UNKNOWN' && !property.canSelfTour;

  // TRADITIONAL always implies canSelfTour=false. This is determined in athena
  const canTourWithAgent = property.displayBadge === 'TRADITIONAL' || isUnknownRequiresAgent;

  // ON_THE_MARKET badge always implies canSelfTour=true. This is determined in athena.
  const isActionable = property.displayBadge === 'ON_THE_MARKET' || canTourWithAgent;

  // Determine if buy direct should be shown
  const showBuyDirect = isBuyDirectEligible(property) && !!property.buyDirectPrice;

  // Check if property has any tags that would disqualify it from BuyDirect
  const hasDisqualifyingTags =
    property.listingTags?.some((tag) => tag !== 'BUY_DIRECT_ELIGIBLE') ?? false;

  // Determine if buy direct button should be disabled (property is under contract)
  const disableBuyDirect =
    property.displayBadge === 'IN_CONTRACT' ||
    property.listingState === 'IN_CONTRACT' ||
    hasDisqualifyingTags;

  // Determine the tour kind based on property data, null if not actionable
  let tourKind: TourKind | null = null;
  if (isActionable) {
    tourKind = canTourWithAgent ? 'tourWithAgent' : 'selfTour';
  }

  return {
    isActionable,
    tourKind,
    showBuyDirect,
    disableBuyDirect,
  };
};
